<template>
  <div class="relative w-full flex flex-col gap-1 p-1 border border-border-normal" style="border-radius: 20px;"
  :class="{'pointer-events-none overflow-hidden': locked}">
    <PreviewLockedOverlay v-if="locked && !loading" class="absolute inset-0 z-10 fade-in" style="border-radius: 20px;"
    :title="`Start Tracking ${brand?.name || 'this brand'} to Unlock Creative Velocity`"
    description="See how many ads are launched and retired over time." />
    <!-- Header & Lookback Toggle -->
    <div class="w-full flex items-center justify-between p-3">
      <div class="flex items-center gap-1">
        <CreativeVelocityIcon class="text-icon-normal" />
        <BaseText type="label" size="sm" class="text-text-muted">
          Meta Creative Velocity
        </BaseText>
      </div>
      <div class="flex items-center gap-0.5 p-0.5 rounded-lg bg-neutral-25">
        <button v-for="option in lookbackOptions" :key="`lookback-option-${option.label}`"
        class="lookback-option-atom" :class="{'active': option.isActive}"
        @click="lookbackDays = option.value">
          <BaseText type="label" size="sm">
            {{ option.label }}
          </BaseText>
        </button>
      </div>
    </div>
    <!-- Category Counts -->
    <div class="w-full grid grid-cols-4 gap-x-2 px-1 pb-1">
      <div v-for="category in categoryCounts" :key="`category-count-${category.label}`"
      class="w-full flex flex-col gap-1 p-3 rounded-xl bg-neutral-25">
        <BaseText type="body" size="sm" class="text-text-normal">
          {{ category.label }}
        </BaseText>
        <div v-if="loading" class="skeleton w-16 h-5 rounded-lg" />
        <BaseText v-else type="label" size="sm" class="text-text-muted fade-in">
          {{ category.value }}
        </BaseText>
      </div>
    </div>
    <!-- Graph Legend -->
    <div class="w-full flex items-center py-0.5 px-2">
      <div class="flex items-center">
        <div class="w-6 h-6 flex items-center justify-center">
          <div class="w-2.5 h-2.5 rounded-sm" style="background-color: #00C7A9;" />
        </div>
        <BaseText type="label" size="sm" class="text-text-muted">
          Launched (New)
        </BaseText>
      </div>
      <div class="flex items-center ml-3">
        <div class="w-6 h-6 flex items-center justify-center">
          <div class="w-2.5 h-2.5 rounded-sm" style="background-color: #DFE1E7;" />
        </div>
        <BaseText type="label" size="sm" class="text-text-muted">
          Retired (Inactive)
        </BaseText>
      </div>
      <div class="flex items-center ml-auto">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M8 16L16 8" stroke="#0097FF" stroke-width="1.5" stroke-linecap="round" stroke-dasharray="2"/>
        </svg>
        <BaseText type="label" size="sm" class="text-text-muted">
          Total Active
        </BaseText>
      </div>
    </div>
    <!-- GRAPH AREA -->
    <div class="relative w-full pt-3" style="height: 348px;">
      <!-- Loading State -->
      <div v-if="loading" class="absolute inset-0 flex flex-col items-center justify-center">
        <BaseLoadingLogo :margin="0"/>
        <BaseText type="label" size="sm" class="skeleton-text mt-1 pb-6">
          Fetching creatives...
        </BaseText>
      </div>
      <VelocityGraph 
        v-else
        class="fade-in"
        :graph-data="creativeVelocityData"
      />
    </div>
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import moment from 'moment'
import { getMetaCreativeVelocityMockData } from '@/utils/spyder/mockDataUtils'

import VelocityGraph from './VelocityGraph.vue'
import PreviewLockedOverlay from '../../PreviewLockedOverlay.vue'
import CreativeVelocityIcon from '@/components/globals/Icons/SpyderV2Icons/CreativeVelocityIcon.vue'

export default {
  name: 'CreativeVelocity',
  components: {
    VelocityGraph,
    PreviewLockedOverlay,
    CreativeVelocityIcon
  },
  props: {
    brand: {
      required: true,
    }
  },
  data () {
    return {
      creativeVelocityData: null,
      lookbackDays: 61,
      loading: true,
    }
  },
  watch: {
    brand: {
      async handler () {
        this.fetchCreativeVelocity()
      },
      immediate: true
    },
    lookbackDays () {
      this.fetchCreativeVelocity()
    }
  },
  computed: {
    locked () {
      const isSubscribed = this.brand?.is_subscribed
      return isSubscribed === undefined ? false : !isSubscribed
    },
    lookbackOptions () {
      return [
        { label: '7d', value: 7, isActive: this.lookbackDays === 7 },
        { label: '14d', value: 15, isActive: this.lookbackDays === 15 },
        { label: '30d', value: 31, isActive: this.lookbackDays === 31 },
        { label: '60d', value: 61, isActive: this.lookbackDays === 61 }
      ]
    },
    categoryCounts () {
      const comp = { totalNewCreatives: 0, totalRetiredCreatives: 0, maxLaunchedCount: 0, netPerDay: [] }
      for (const item of this.creativeVelocityData ?? []) {
        comp.totalNewCreatives += item.launched_count
        comp.totalRetiredCreatives += item.paused_count
        comp.maxLaunchedCount = Math.max(comp.maxLaunchedCount, item.launched_count)
        comp.netPerDay.push(item.launched_count - item.paused_count)
      }
      const values = {
        newCreatives: comp.totalNewCreatives,
        netChange: comp.totalNewCreatives - comp.totalRetiredCreatives,
        peakDay: comp.maxLaunchedCount,
        avgPerDay: comp.netPerDay.reduce((acc, curr) => acc + curr, 0) / comp.netPerDay.length
      }

      return [
        { label: 'Ads Launched', value: values.newCreatives.toLocaleString() },
        { label: 'Net Change', value: `${values.netChange > 0 ? '+' : ''}${values.netChange.toLocaleString()}` },
        { label: 'Peak Day', value: values.peakDay.toLocaleString() },
        { label: 'Avg / day', value: values.avgPerDay.toFixed(2) },
      ]
    }
  },
  methods: {
    async fetchCreativeVelocity () {
      if (!this.brand?.id) return
      if (!this.brand.is_subscribed) {
        this.creativeVelocityData = getMetaCreativeVelocityMockData()
        this.loading = false
        return
      }
      this.loading = true
      try {
        // End date starts two days ago
        const endDate = moment().subtract(2, 'days').format('YYYY-MM-DD')
        // Start date is the end date minus the lookback days
        const startDate = moment(endDate).subtract(this.lookbackDays - 1, 'days').format('YYYY-MM-DD')
        // Fetch the creative velocity data
        this.creativeVelocityData = await ForeplayAPI.Brands.getBrandCreativeVelocity(
          this.brand.id, 
          { 
            startDate, 
            endDate
          }
        )
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch creative velocity data'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.lookback-option-atom {
  padding: 2px 6px 2px 6px;
  border-radius: 6px;
  color: #5C667A; /* text-normal */
  background-color: transparent;
  box-shadow: none;
  transition: color 100ms ease-in-out, background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.lookback-option-atom:hover:not(.active) {
  background-color: #ECEFF3; /* neutral-50 */
}
.lookback-option-atom.active {
  color: #2F3547; /* text-muted */
  background-color: white;
  box-shadow: 0 3px 3px -1.5px rgba(6, 7, 16, 0.04), 0 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.08), 0 0.25px 1.5px 0.75px rgba(6, 7, 16, 0.04), 0 0 0 0.5px rgba(6, 7, 16, 0.02);
}

.fade-in {
  animation: fadeIn 250ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>